import React, { ReactNode, useContext, useState } from "react";
import { Button, MCard } from "components/Shared";
import { TextField } from "react-md";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";
import IconButton from "components/Shared/IconButton";
import { classes } from "components/Utility";
import { deepCopy } from "./helpers";
import { setObjectValue } from "components/Shared/Functions";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { showExpressionEditor } from "./CodeEditor/ExpressionEditor";

export interface EnumerationEditorField<T> {
  label: string,
  property: keyof T,
  required?: boolean,
  isExpression?: boolean,
  blockCommas?: boolean,
  hideOnAdd?: boolean,
};

interface Props<T> {
  title?: string,
  items: T[],
  fields: EnumerationEditorField<T>[],
  bulkEditFields?: (keyof T)[],
  onChange: (items: T[]) => void,
  displayItem: (item: T) => ReactNode,
  preAddLogic?: (item: T) => void,
  noCard?: boolean,
};

const reorderHandleClassname = "reorder-handle";

const FormEnumerationEditor = <T,>({ title, items, fields, bulkEditFields, onChange, displayItem, noCard, preAddLogic }: Props<T>) => {
  const getEmptyItem = () => {
    let item = {} as T;
    fields.forEach(f => setObjectValue(item, f.property, ""));
    return item;
  };
  
  const [newItem, setNewItem] = useState<T>(getEmptyItem);
  const [editItem, setEditItem] = useState<T>(null);
  const [listEditIndex, setListEditIndex] = useState<number>();

  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkEditValue, setBulkEditValue] = useState("");

  const startEditListItem = (index: number) => {
    setEditItem(deepCopy(items[index]));
    setListEditIndex(index);
  };

  const stopEditListItem = (save: boolean) => {
    if (save) {
      let newItems = deepCopy(items);
      newItems[listEditIndex] = editItem;
      onChange(newItems);
    }

    setListEditIndex(null);
    setEditItem(null);
  };

  const addNewListItem = () => {
    let newItems = deepCopy(items);
    preAddLogic?.(newItem);
    newItems.push(newItem);
    onChange(newItems);
    setNewItem(getEmptyItem);
  };

  const deleteListItem = (index: number) => {
    let newItems = deepCopy(items);
    newItems.splice(index, 1);
    onChange(newItems);
  };
  
  const startBulkEditing = () => {
    setBulkEditValue(items.map(item => bulkEditFields.map(k => item[k]).join("|")).join("\n"));
    setIsBulkEditing(true);
  };

  const stopBulkEditing = (save: boolean) => {
    if (save) {
      onChange(bulkEditValue.split("\n").map(line => {
        let split = line.split("|");
        return split.reduce((acc, val, i) => {
          acc[bulkEditFields[i]] = val;
          return acc;
          }, {} as any);
      }));
    }

    setBulkEditValue("");
    setIsBulkEditing(false);
  };

  let children: ReactNode;
  if (isBulkEditing) {
    children = <>
      <TextField
        rows={10}
        fullWidth
        maxRows={15}
        value={bulkEditValue}
        onChange={(newValue: string) => setBulkEditValue(newValue)}
      />
      <div className="bottom-buttons">
        <Button onClick={() => stopBulkEditing(true)}>Save</Button>
        <Button onClick={() => stopBulkEditing(false)}>Cancel</Button>
      </div>
    </>;
  } else {
    const onEditChanged = (item: T, setItem: (val: T) => void) => (prop, val) => {
      item = deepCopy(item);
      setObjectValue(item, prop, val);
      setItem(item);
    };

    let reorderItems: ReorderableItem[] = items.map((item, i) => (
      {
        id: `${i}`,
        node: (
          <div className={classes("question-list-item", i === listEditIndex && "editing")}>
            {i === listEditIndex ?
            <>
              <ItemEditor
                fields={fields}
                item={editItem}
                onChange={onEditChanged(editItem, setEditItem)}
              />
              <div className="bottom-buttons">
                <Button onClick={() => stopEditListItem(true)}>Save</Button>
                <Button onClick={() => stopEditListItem(false)}>Cancel</Button>
              </div>
            </>
            :
            <>
              <IconButton icon="reorder" className={reorderHandleClassname} />
              <span>{displayItem(item)}</span>
              <IconButton icon="edit" onClick={() => startEditListItem(i)} />
              <IconButton icon="delete" onClick={() => deleteListItem(i)} />
            </>}
          </div>)
      }
    ));

    // make sure all required fields are filled out
    let blockAdd = fields.some(f => f.required && !(newItem[f.property] as string)?.trim());

    // if no fields are required, then just make sure one field has a value to avoid completely empty entries
    if (fields.every(f => !f.required)) {
      blockAdd = fields.every(f => !newItem[f.property]);
    }

    children = <>
      <ReorderableGrid
        items={reorderItems}
        onReorder={newOrder => onChange(newOrder.map(i => items[i]))}
        handleSelector={`.${reorderHandleClassname}`}
      />
      <ItemEditor
        fields={fields}
        item={newItem}
        onChange={onEditChanged(newItem, setNewItem)}
        isAdding
      />
      <div className="bottom-buttons">
        {bulkEditFields && <Button onClick={startBulkEditing}>Bulk Edit</Button>}
        <Button onClick={addNewListItem} disabled={blockAdd}>Add</Button>
      </div>
    </>;
  }

  const className = "enumeration-item-editor";

  if (noCard) return <div className={className}>{children}</div>;

  return (
    <MCard title={title} cardContentClassName={className}>
      {children}
    </MCard>
  );
};

interface ItemEditorProps<T> {
  fields: EnumerationEditorField<T>[],
  isAdding?: boolean,
  item: any,
  onChange: (property: keyof T, newValue: string) => void,
};

const ItemEditor = <T,>({ fields, item, onChange, isAdding }: ItemEditorProps<T>) => {
  const dialog = useContext<DialogProviderValue>(DialogContext);

  const onChangeWrapped = (field: EnumerationEditorField<T>) => (newValue: string) => {
    // @ts-ignore i promise replace all exists
    if (field.blockCommas) newValue = newValue.replaceAll(",", "");
    onChange(field.property, newValue);
  };

  const showExpressionEditorLocal = (field: EnumerationEditorField<T>) =>
    showExpressionEditor(dialog, undefined, item[field.property] as string, onChangeWrapped(field));

  return fields.filter(f => !isAdding || !f.hideOnAdd).map(f =>
    <div className="enumeration-editor-property-editor">
      <TextField
        label={f.label}
        onChange={onChangeWrapped(f)}
        value={item[f.property]}
      />
      {f.isExpression && <IconButton icon="code" onClick={() => showExpressionEditorLocal(f)} />}
    </div>
  );
};

export default FormEnumerationEditor;
